/**
 * Public Scan Page ViewModel
 *
 * STRICT MVVM PATTERN - ALL presentation logic in ViewModel
 * Returns: { scanContext, loading, error, computed properties, event handlers }
 *
 * Handles public NFC scan page logic:
 * - Reads viewer NFC scan context from Redux (fetched by NfcScanPage wrapper)
 * - Determines if item is claimed and public (show profile button)
 * - Extracts owner info (username, avatar) from scan context
 * - Extracts product info (title, image) from scan context
 * - Creates handleViewProfile callback for public items only
 *
 * IMPORTANT: ViewModel does NOT fetch data (wrapper does that)
 * This ensures single API call guarantee (Story 3.7.c AC #10)
 *
 * Story 3.7.c - MVVM presentation logic for PublicScanPage component
 */

import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useAppSelector } from "@/store/hooks";
import {
  selectScanContext,
  selectScanLoading,
  selectScanError,
} from "@/store/slices/publicScanSlice";
import {
  isInvalidSignature,
  isClaimedItem,
  isStolenItem,
} from "@/domain/types/ViewerNfcScanContext";
import type { ViewerNfcScanContextResponse } from "@/domain/types/ViewerNfcScanContext";

/**
 * ViewModel return type
 */
export interface PublicScanViewModel {
  // Raw data from Redux
  scanContext: ViewerNfcScanContextResponse | null;
  loading: boolean;
  error: string | null;

  // Computed properties (presentation logic)
  isClaimedAndPublic: boolean;
  ownerUsername: string | null;
  ownerAvatar: string | null;
  productTitle: string | null;
  productImage: string | null;
  partnerLink: { url: string; label?: string } | undefined;
  /** Item number from scan context (0 if not available) */
  productNumber: number;
  /** Total edition size from scan context (0 if not available) */
  totalProductNumber: number;
  /** Shopify URL for this product */
  shopifyUrl: string;

  // Event handlers
  handleViewProfile: (() => void) | undefined;
}

/**
 * usePublicScanViewModel Hook
 *
 * All presentation logic for Public Scan page.
 * React Compiler automatically memoizes functions.
 *
 * IMPORTANT: Does NOT fetch data (reads from Redux only)
 * NfcScanPage wrapper is responsible for fetching scan context.
 */
export function usePublicScanViewModel(): PublicScanViewModel {
  const navigate = useNavigate();

  // Redux state
  const scanContext = useAppSelector(selectScanContext);
  const loading = useAppSelector(selectScanLoading);
  const error = useAppSelector(selectScanError);

  // Computed: Is item claimed? (button shows for all claimed items, regardless of visibility)
  const isClaimedAndPublic =
    scanContext !== null &&
    isClaimedItem(scanContext);

  // Computed: Owner username (from claimed or stolen items)
  const ownerUsername =
    scanContext !== null &&
    (isClaimedItem(scanContext) || isStolenItem(scanContext))
      ? scanContext.ownerInfo.username
      : null;

  // Computed: Owner avatar (from claimed or stolen items)
  const ownerAvatar =
    scanContext !== null &&
    (isClaimedItem(scanContext) || isStolenItem(scanContext))
      ? scanContext.ownerInfo.avatar || null
      : null;

  // Computed: Product title (from all valid contexts except invalid signature)
  const productTitle =
    scanContext !== null && !isInvalidSignature(scanContext)
      ? scanContext.productInfo.title
      : null;

  // Computed: Product image (from all valid contexts except invalid signature)
  const productImage =
    scanContext !== null && !isInvalidSignature(scanContext)
      ? scanContext.productInfo.imageUrl
      : null;

  // Computed: Partner link (Story 3.7.b: from LinkedNfc.partnerLink)
  const partnerLink =
    scanContext !== null &&
    !isInvalidSignature(scanContext) &&
    isClaimedItem(scanContext) &&
    scanContext.partnerLink
      ? {
          url: scanContext.partnerLink,
          label: undefined, // No label for partner permanent links
        }
      : undefined;

  // Event handler: Navigate to profile (for all claimed items)
  const handleViewProfile = useCallback(() => {
    if (
      scanContext &&
      isClaimedItem(scanContext) &&
      scanContext.ownerInfo.userId
    ) {
      navigate(`/trophy-room/${scanContext.ownerInfo.userId}`);
    }
  }, [scanContext, navigate]);

  return {
    // Raw data
    scanContext,
    loading,
    error,

    // Computed properties
    isClaimedAndPublic,
    ownerUsername,
    ownerAvatar,
    productTitle,
    productImage,
    partnerLink,
    productNumber:
      scanContext !== null && !isInvalidSignature(scanContext)
        ? scanContext.productInfo.itemNumber ?? 0
        : 0,
    totalProductNumber:
      scanContext !== null && !isInvalidSignature(scanContext)
        ? scanContext.productInfo.editionSize ?? 0
        : 0,
    shopifyUrl: "https://anyw3ar.com/",

    // Event handlers (undefined for non-public items)
    handleViewProfile: isClaimedAndPublic ? handleViewProfile : undefined,
  };
}
